import { React, moment, bind } from '../../../Imports';
import { Checkbox, TableCell, TableRow } from '../../../MaterialUIComponents';
import * as DateFormatter from '../../Shared/DateFormatter';
import { getStatusLabel } from '$Utilities/videoEventStatusUtility';
import { getEventTypeLabels } from '$Utilities/videoEventTypeUtility';

import { IIntegrationPartnerDataInjectedProps, IntegrationPartnerDataService } from '../../../state/IntegrationPartnerDataFreezerService';
import { IConfigServiceInjectedProps, ConfigService } from '../../../state/ConfigFreezerService';
import { RolesEnum, canEditByRole } from '../../../externals/VerifyRole';
import { VideoEventResponse, VideoEventWorkflowStatusIdEnum, UserResponse } from '$Generated/api';
import * as _ from 'lodash';
import { EllipsisPopperMenu, EllipsisItem } from '$Components/Shared/EllipsisPopperMenu';
import VideoEventFlagButton from '../VideoEventFlagButton/VideoEventFlagButton';

const styles = require('./VideoEventListRow.scss') as {
    row: string;
    rowSelected: string;
    checkbox: string;
    dismissCheckboxSizing: string;
    eventType: string;
    eventTypeIconCell: string;
    videoEventStatus: string;
    driver: string;
    vehicle: string;
    date: string;
    mph: string;
    mphChangeValue: string;
    actions: string;
    eventActions: string;
    flexRow: string;
    action: string;
    location: string;
    eventTypeIcon: string;
    mphSingleSpeed: string;
    videoEventFlaggedByUsersCaption: string;
    flexRowAllFlagged: string;
    flagButtonBadge: string;
    flagButtonActive: string;
    flagStatus: string;
    flexEllipsisRow: string;
    serialNumberGeotab: string;
    serialNumberEncompass: string;
};

interface IVideoEventListRowProps {
    event: VideoEventResponse;
    selectedEventHandler: (id: number) => void;
    selectedEvents: number[];
    onSelectVideoEvent: (eventId: string, goBack: boolean) => void;
    onFlagVideoEvent?: (eventId: number | undefined) => void;
    onRateVideo?: (eventId: number | undefined, rating: number, status: string) => void;
    onDismissVideo?: (eventId: number | undefined) => void;
    onClearRating?: (eventid: number | undefined) => void;
    onReopenEvent?: (eventid: number | undefined) => void;
    videoEventStatus?: VideoEventWorkflowStatusIdEnum | undefined;
    showCompanyLevel?: boolean;
    toggleDeletePopup: (selectedId?: number) => void;
    toggleDismissPopup: (selectedId?: number) => void;
    toggleRateEventPopup: (selectedId: number) => void;
    deleteButtonDisabled: boolean;
}

interface IVideoEventListRowState {
    selectedRating: number | undefined | null;
    flaggedByUsers: Array<UserResponse>;
    isDismissed: boolean;
    canRate: boolean;
    promotedBy: string;
    allSelected: boolean;
}

export const scoreOptions = [
    { value: '-1', label: '-1 (Low Risk)' },
    { value: '-2', label: '-2 (Moderate Risk)' },
    { value: '-3', label: '-3 (High Risk)' },
    { value: '-4', label: '-4 (Critical)' },
    { value: '1', label: '+1' },
    { value: '2', label: '+2' },
    { value: '3', label: '+3' },
];

class _VideoEventListRow extends React.PureComponent<
    IVideoEventListRowProps & IConfigServiceInjectedProps & IIntegrationPartnerDataInjectedProps,
    IVideoEventListRowState
> {
    state: IVideoEventListRowState = {
        selectedRating: this.props.event.score,
        flaggedByUsers: this.props.event.flaggedByUsers ?? ([] as UserResponse[]),
        isDismissed: this.props.event.videoEventStatus == 'Dismissed',
        canRate: this.props.event.videoEventStatus !== 'Dismissed' && this.props.event.videoEventStatus !== 'Completed',
        promotedBy: '',
        allSelected: false,
    };

    @bind
    onClearRating(eventId: number | undefined): void {
        if (this.props.onClearRating) {
            this.setState({
                isDismissed: false,
                selectedRating: undefined,
                canRate: true,
            });
            this.props.onClearRating(eventId);
        }
    }

    @bind
    async onReopenEvent(eventId: number | undefined): Promise<void> {
        if (this.props.onReopenEvent) {
            this.setState({
                isDismissed: false,
                selectedRating: undefined,
                canRate: true,
            });
            this.props.onReopenEvent(eventId);
        }
    }

    @bind
    onDismissVideo(eventId: number | undefined): void {
        if (this.props.onDismissVideo) {
            this.setState({
                isDismissed: true,
                selectedRating: null,
                canRate: false,
            });
            this.props.onDismissVideo(eventId);
        }
    }

    @bind
    eventIsFlaggedByCurrentUser(): boolean {
        return (
            _.find(this.state.flaggedByUsers, (user: UserResponse) => {
                return user.integrationProviderSourceId === IntegrationPartnerDataService.getState().currentUserResult.data.id;
            }) !== undefined
        );
    }

    @bind
    onRateVideo(eventId: number | undefined, rating: number, status: string): void {
        if (this.props.onRateVideo && this.state.canRate) {
            this.setState({
                selectedRating: rating,
                isDismissed: false,
            });
            this.props.onRateVideo(eventId, rating, status);
        }
    }

    @bind
    onFlagVideo(eventId: number | undefined): void {
        if (!canEditByRole([RolesEnum.videoEventEdit, RolesEnum.encompassVideo])) return;
        if (this.props.onFlagVideoEvent) {
            const currentUser = IntegrationPartnerDataService.getState().currentUserResult.data;
            if (this.eventIsFlaggedByCurrentUser()) {
                this.setState({
                    flaggedByUsers: _.filter(this.state.flaggedByUsers, (user: UserResponse) => {
                        return user.email !== currentUser.name;
                    }),
                });
            } else {
                this.setState({
                    flaggedByUsers: this.state.flaggedByUsers.concat([
                        {
                            id: '',
                            email: currentUser.name,
                            integrationProviderSourceId: currentUser.id,
                            displayName: `${currentUser.firstName} ${currentUser.lastName}`,
                        },
                    ]),
                });
            }

            this.props.onFlagVideoEvent(eventId);
        }
    }

    @bind
    goToDetails(eventId: string): void {
        this.props.onSelectVideoEvent(eventId, false);
    }

    @bind
    formatGforceEventType(type: string | undefined): string {
        let eventType = String(type);
        eventType = eventType?.includes('Gforce') ? eventType.replace('Gforce', '') : eventType;

        if (eventType?.includes('RightTurn')) {
            eventType = eventType.replace('RightTurn', 'Cornering');
        }

        return eventType;
    }

    @bind
    formatEventTypeToCommaSeperatedValue(eventTypes: string[] | undefined): string {
        if (eventTypes === undefined || eventTypes === null) {
            return 'Unknown';
        }

        const eventTypeLabelArray = eventTypes?.map((x) => {
            if (['forwardcollisionwarning', 'forward collision warning'].indexOf(x.toLowerCase()) !== -1) {
                // Forward Collision Warning and Headway Monitoring are also too long for the title card
                return 'FCW';
            }

            if (x.includes('Gforce')) {
                // Cleaning Gforce reference that comes from VideoEventTypeEnum
                x = x.replace('Gforce', '');
            }

            if (x?.includes('RightTurn')) {
                x = x.replace('RightTurn', 'Cornering');
            }

            x = String(x)
                .replace(/([A-Z])/g, ' $1')
                .trim();
            return x;
        });

        const labels = getEventTypeLabels(eventTypeLabelArray);
        return labels?.join(', ');
    }

    render(): JSX.Element {
        const isSelected = this.props.selectedEvents.some((eventId: any) => eventId === this.props.event.id);
        const canEdit = canEditByRole([RolesEnum.videoEventEdit, RolesEnum.encompassVideo]);
        const displayEventId = this.props.event.displayId || '';
        const eventStatus = this.props.videoEventStatus !== undefined ? getStatusLabel(this.props.videoEventStatus) : 'Unknown';
        const eventType = this.props.event.videoEventTypePairings?.map((event) => {
            return this.formatGforceEventType(event.videoEventTypeString);
        });
        const eventTypeString = this.formatEventTypeToCommaSeperatedValue(eventType);
        const isGeotab = this.props.config.isIntegrationPlatformGeotab();

        const ellipsisMenuitems = [
            {
                key: 'score',
                text: 'Score',
                isHidden: this.props.videoEventStatus === 'Dismissed' || this.props.videoEventStatus === 'Completed' || !canEdit,
                onClick: () => this.props.toggleRateEventPopup(this.props.event.id ?? 0),
            },
            {
                key: 'dismiss',
                text: 'Dismiss',
                isHidden: this.props.videoEventStatus === 'Dismissed' || this.props.videoEventStatus === 'Completed' || !canEdit,
                onClick: () => this.props.toggleDismissPopup(this.props.event.id ?? 0),
            },
            {
                key: 'delete',
                text: 'Delete',
                isHidden: !canEditByRole([RolesEnum.videoAdmin]),
                onClick: () => this.props.toggleDeletePopup(this.props.event.id ?? 0),
                isDisabled: this.props.deleteButtonDisabled || !canEditByRole([RolesEnum.videoAdmin]),
            },
        ] as EllipsisItem[];

        return (
            <TableRow className={`${styles.row} ${isSelected ? styles.rowSelected : ''}`}>
                {canEdit && (
                    <TableCell className={` ${isSelected ? styles.rowSelected : ''} `}>
                        {eventStatus !== 'Dismissed' && (
                            <Checkbox
                                style={{ transform: 'scale(0.75)', marginRight: 8, marginLeft: 8, padding: 0 }}
                                className={styles.checkbox}
                                onClick={() => this.props.selectedEventHandler(this.props.event.id!)}
                                checked={isSelected}
                                disableRipple={true}
                            />
                        )}
                    </TableCell>
                )}

                <TableCell
                    className={`${styles.eventType} ${isSelected ? styles.rowSelected : ''}`}
                    onClick={(): void => this.goToDetails(displayEventId)}
                >
                    {eventTypeString}
                </TableCell>

                {this.props.showCompanyLevel && (
                    <TableCell
                        className={`${isSelected ? styles.rowSelected : ''}`}
                        onClick={(): void => this.goToDetails(displayEventId)}
                    >
                        {this.props.event.companyLevelName?.trim() ? this.props.event.companyLevelName : '—'}
                    </TableCell>
                )}

                <TableCell className={`${styles.flagStatus} ${isSelected ? styles.rowSelected : ''}`}>
                    <div>
                        <VideoEventFlagButton
                            videoEventId={this.props.event.id ?? 0}
                            flaggedByUsers={this.state.flaggedByUsers}
                            onClick={this.onFlagVideo}
                            showLabel
                            userCanEdit={canEdit}
                            labelSize="12px"
                        />
                    </div>
                </TableCell>

                <TableCell
                    className={`${styles.videoEventStatus} ${isSelected ? styles.rowSelected : ''}`}
                    onClick={(): void => this.goToDetails(displayEventId)}
                >
                    {eventStatus}
                </TableCell>

                <TableCell
                    className={`${styles.driver} ${isSelected ? styles.rowSelected : ''}`}
                    onClick={(): void => this.goToDetails(displayEventId)}
                >
                    {this.props.event.driverName?.trim() ? this.props.event.driverName : 'Unknown'}
                </TableCell>

                <TableCell
                    className={`${styles.vehicle} ${isSelected ? styles.rowSelected : ''}`}
                    onClick={(): void => this.goToDetails(displayEventId)}
                >
                    {this.props.event.vehicleDescription || 'Unknown'}
                    <div className={isGeotab ? styles.serialNumberGeotab : styles.serialNumberEncompass}>
                        {this.props.event.cameraDeviceSerialNumber}
                    </div>
                </TableCell>

                <TableCell
                    className={`${styles.date} ${isSelected ? styles.rowSelected : ''}`}
                    onClick={(): void => this.goToDetails(displayEventId)}
                >
                    <div>{DateFormatter.date(moment(this.props.event.eventStartDate) || moment())} </div>
                    <div>{DateFormatter.timeWithSecondsAndTimezone(moment(this.props.event.eventStartDate) || moment())}</div>
                </TableCell>
                <TableCell
                    className={`${styles.location} ${isSelected ? styles.rowSelected : ''}`}
                    onClick={(): void => this.goToDetails(displayEventId)}
                >
                    <div>
                        {this.props.event.addressStreetNumber || ''} {this.props.event.addressStreetName || ''}
                    </div>
                    <div>
                        {this.props.event.addressCityName || 'Unknown'}, {this.props.event.addressRegion || 'Unknown'}
                    </div>
                </TableCell>
                <TableCell
                    className={`${styles.vehicle} ${isSelected ? styles.rowSelected : ''}`}
                    onClick={(): void => this.goToDetails(displayEventId)}
                >
                    {this.props.event.score
                        ? (scoreOptions.find((x) => x.value == this.props.event.score?.toString())?.label ?? '')
                        : '\u2014'}
                </TableCell>
                <TableCell
                    className={`${styles.videoEventStatus} ${isSelected ? styles.rowSelected : ''}`}
                    onClick={(): void => this.goToDetails(displayEventId)}
                >
                    {displayEventId}
                </TableCell>

                <TableCell
                    className={[styles.flexEllipsisRow, styles.flexRowAllFlagged, isSelected ? styles.rowSelected : ''].join(' ')}
                    onClick={(): void => this.goToDetails(displayEventId)}
                    style={{ padding: canEdit ? '0px 16px' : '0px', textAlign: 'right' }}
                >
                    {(this.props.event.workflowStep === 'Success' ||
                        this.props.event.workflowStep === 'PreparedIdle' ||
                        this.props.event.workflowStep === 'Failed') && (
                        <div>
                            {ellipsisMenuitems.findIndex((item) => !item.isHidden) !== -1 && (
                                <EllipsisPopperMenu items={ellipsisMenuitems} isEncompass={!isGeotab} />
                            )}
                        </div>
                    )}
                </TableCell>
            </TableRow>
        );
    }
}

export const VideoEventListRow = ConfigService.inject(IntegrationPartnerDataService.inject(_VideoEventListRow));
